import React, { useState, lazy } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ExternalLink, ChevronDown, ChevronUp } from 'lucide-react';
interface Publication {
  publication: string;
  date: string;
  title: string;
  byline: string;
  summary: string;
  url: string;
  image?: string;
}
// Add future publications here (most recent first) — each entry renders automatically.
const publications: Publication[] = [{
  publication: 'Chair X',
  date: '2019 – 2025',
  title: 'Hydrogen: From Hype to Pragmatism (2019–2025)',
  byline: 'Dr Richard M Oblath FIMMM, Chair of the IOM3 Energy Transition Group',
  summary: 'A retrospective tracing the evolution of the hydrogen economy from the exuberant optimism of 2019 to a more sober, pragmatic outlook by 2025 — examining where green hydrogen and its derivatives have genuinely gained traction, where expectations outpaced reality, and what a realistic role for hydrogen looks like across the hardest-to-abate sectors.',
  url: 'https://acrobat.adobe.com/id/urn:aaid:sc:EU:04bc087f-83c2-42fa-80b3-0095dd15c66e'
}, {
  publication: 'IOM3 Materials World',
  date: '9 October 2023',
  title: 'Transforming the Energy System to Hit Net-Zero',
  byline: 'Dr Richard M Oblath FIMMM, Chair of the IOM3 Energy Transition Group',
  summary: 'Written a year on from the previous piece, this article takes stock of global and UK progress toward the 2050 net-zero target ahead of COP28 — covering the state of clean energy investment, the pace of renewable and nuclear deployment, the continuing role of oil and gas during the transition, and the specific policy and infrastructure gaps (grid capacity, storage, hydrogen) that need urgent attention in the UK.',
  url: 'https://www.iom3.org/resource/transforming-the-energy-system-to-hit-net-zero.html',
  image: 'https://cdn.magicpatterns.com/uploads/4vtPTTcnQeusRQ3ASNQ9xd/2098567a-631a-4856-bd70-d92c22037899.jpg'
}, {
  publication: 'IOM3 Materials World',
  date: '5 May 2022',
  title: 'Age of Uncertainty – Energy Security and Transitioning to Net-Zero',
  byline: 'Dr Richard M Oblath FIMMM, Chair of the IOM3 Energy Transition Group',
  summary: 'Written against the backdrop of the invasion of Ukraine and the resulting energy price shock, this article argues that short-term energy security pressures should not derail the longer-term, slower-moving transition to a lower-emission global energy system, and reflects on how the two challenges need to be tackled in parallel rather than in competition with one another.',
  url: 'https://www.iom3.org/resource/age-of-uncertainty-energy-security-and-transitioning-to-net-zero.html',
  image: 'https://cdn.magicpatterns.com/uploads/nuGunp3shzgkZSjgLdoCKZ/yfjm.png'
}];
const VISIBLE_COUNT = 4;
function PublicationCard({
  item
}: {
  item: Publication;
}) {
  return <article className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8 py-10" data-id="element-212">
      {/* Publication wordmark + optional featured image */}
      <div className="md:col-span-3" data-id="element-213">
        {item.image && <img src={item.image} alt={`${item.byline.split(',')[0]} — featured in ${item.publication}`} loading="lazy" decoding="async" className="w-28 h-28 object-cover object-center rounded-lg mb-4 ring-1 ring-gray-200" data-id="element-214" />}
        <span className="inline-block text-xs font-semibold uppercase tracking-[0.15em] text-gray-400 border border-gray-200 rounded px-3 py-2 grayscale" data-id="element-215">
          {item.publication}
        </span>
        <p className="text-xs text-gray-400 mt-3" data-id="element-216">{item.date}</p>
      </div>

      {/* Citation content */}
      <div className="md:col-span-9" data-id="element-217">
        <h3 className="font-serif text-xl md:text-2xl text-navy leading-snug mb-2" data-id="element-218">
          {item.title}
        </h3>
        <p className="text-sm font-medium text-navy-light mb-1" data-id="element-219">
          {item.byline}
        </p>
        <p className="text-xs uppercase tracking-wider text-gray-400 mb-4" data-id="element-220">
          {item.publication} · {item.date}
        </p>
        <p className="text-gray-600 text-sm md:text-base leading-relaxed mb-5" data-id="element-221">
          {item.summary}
        </p>
        <a href={item.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-sm font-medium text-gold hover:text-navy transition-colors" data-id="element-222">
          Read Article
          <ExternalLink size={15} data-id="element-223" />
        </a>
      </div>
    </article>;
}
export function ThoughtLeadership() {
  const [expanded, setExpanded] = useState(false);
  const hasMore = publications.length > VISIBLE_COUNT;
  const primary = hasMore ? publications.slice(0, VISIBLE_COUNT) : publications;
  const overflow = hasMore ? publications.slice(VISIBLE_COUNT) : [];
  return <section id="publications" className="py-24 bg-white" data-id="element-224">
      <div className="max-w-5xl mx-auto px-6 md:px-12" data-id="element-225">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-100px'
      }} transition={{
        duration: 0.6
      }} data-id="element-226">
          <div className="mb-4" data-id="element-227">
            <h2 className="font-serif text-3xl md:text-4xl text-navy mb-6" data-id="element-228">
              Publications &amp; Thought Leadership
            </h2>
            <div className="w-16 h-px bg-gold mb-6" data-id="element-229"></div>
          </div>
          <p className="text-gray-500 md:text-lg mb-12 max-w-2xl" data-id="element-230">
            Selected commentary on energy security, decarbonization, and the
            energy transition.
          </p>

          <div className="divide-y divide-gray-200 border-t border-gray-200" data-id="element-231">
            {primary.map((item, idx) => <PublicationCard key={idx} item={item} data-id="element-232" />)}

            <AnimatePresence data-id="element-233">
              {expanded && overflow.map((item, idx) => <motion.div key={`overflow-${idx}`} initial={{
              opacity: 0,
              height: 0
            }} animate={{
              opacity: 1,
              height: 'auto'
            }} exit={{
              opacity: 0,
              height: 0
            }} transition={{
              duration: 0.35
            }} className="overflow-hidden border-t border-gray-200" data-id="element-234">
                    <PublicationCard item={item} data-id="element-235" />
                  </motion.div>)}
            </AnimatePresence>
          </div>

          {hasMore && <button onClick={() => setExpanded(!expanded)} className="mt-10 inline-flex items-center gap-2 text-sm font-medium text-navy hover:text-gold transition-colors" data-id="element-236">
              {expanded ? 'Show Fewer' : `View All Publications (${publications.length})`}
              {expanded ? <ChevronUp size={16} data-id="element-237" /> : <ChevronDown size={16} data-id="element-238" />}
            </button>}
        </motion.div>
      </div>
    </section>;
}